/**
 * Route-Level RBAC Configuration
 *
 * Defines permission requirements for routes across all portals.
 * Used by PermissionRoute component to guard route access.
 *
 * @see Issue #428 - Implement route-level RBAC for portal navigation
 */

import { Permission } from '../enums/permissions';
import { UserRole } from '../enums/roles';

// ============================================================================
// Types
// ============================================================================

/**
 * Configuration for route-level permission requirements.
 * Similar to LockConfig but specifically for route guarding.
 */
export interface RoutePermissionConfig {
  /** Route path pattern (supports wildcards) */
  path: string;

  /** Single permission required to access this route */
  requiredPermission?: string;

  /** Any of these permissions grants access (OR logic) */
  requiredAnyPermission?: string[];

  /** All of these permissions required (AND logic) */
  requiredAllPermissions?: string[];

  /** Specific roles that can access this route */
  requiredRoles?: UserRole[];

  /** If true, user must have ALL specified roles (default: any role grants access) */
  requireAllRoles?: boolean;

  /** Redirect path when unauthorized (default: '/unauthorized') */
  unauthorizedRedirect?: string;

  /** Human-readable reason for restriction (for logging/debugging) */
  reason?: string;
}

/**
 * Route configuration with permission requirements
 * Note: The element type is generic to avoid React dependency in shared-types.
 * Use this in your portal code with proper typing.
 */
export interface ProtectedRouteConfig<T = unknown> {
  path: string;
  element: T;
  permissions?: RoutePermissionConfig;
}

// ============================================================================
// Business Portal Route Permissions
// ============================================================================

/**
 * Route permission mappings for Business Portal.
 *
 * Routes without entries here are accessible to all authenticated users.
 * The backend ALWAYS enforces permissions - this is for UX only.
 */
export const BUSINESS_PORTAL_ROUTE_PERMISSIONS: Record<string, RoutePermissionConfig> = {
  // ============================================================================
  // Dashboard (Issue #431 - Gap Analysis)
  // ============================================================================
  '/dashboard': {
    path: '/dashboard',
    // Dashboard accessible to all authenticated users - no specific permission required
    // This entry exists for completeness and documentation
    reason: 'Dashboard is accessible to all authenticated users',
  },

  '/watchlist': {
    path: '/watchlist',
    // Watchlist surfaces the user's own pinned entities — every authenticated
    // user has their own list, so no specific permission gate. Added when the
    // /watchlist management page split out from the home Watchlist card.
    reason: 'Personal watchlist is accessible to all authenticated users',
  },

  // ============================================================================
  // Astor AI Section
  // ============================================================================
  '/astor': {
    path: '/astor',
    requiredAnyPermission: [Permission.agents.read],
    reason: 'AI chat requires agent read permission',
  },
  '/astor/chat': {
    path: '/astor/chat',
    requiredAnyPermission: [Permission.agents.read, Permission.conversations.read],
    reason: 'AI chat requires agent or conversation read permission',
  },

  // ============================================================================
  // Control Center Section - Administrative Routes
  // ============================================================================
  '/control-center/astor-agents': {
    path: '/control-center/astor-agents',
    requiredAnyPermission: [Permission.agents.read],
    reason: 'Agent settings require agent read permission',
  },
  '/control-center/astor-agents/create': {
    path: '/control-center/astor-agents/create',
    requiredAnyPermission: [Permission.agents.create],
    reason: 'Creating agents requires agent create permission',
  },
  '/control-center/roles': {
    path: '/control-center/roles',
    requiredAnyPermission: [Permission.users.read],
    reason: 'Role management requires user read permission',
  },
  '/control-center/permissions': {
    path: '/control-center/permissions',
    requiredAnyPermission: [Permission.users.read],
    reason: 'Permission management requires user read permission',
  },
  '/control-center/webhooks': {
    path: '/control-center/webhooks',
    requiredAnyPermission: [
      Permission.settings.webhooks.create,
      Permission.settings.webhooks.update,
      Permission.settings.webhooks.delete,
    ],
    reason: 'Webhook settings require webhook permissions',
  },
  '/control-center/developer-api': {
    path: '/control-center/developer-api',
    requiredAnyPermission: [Permission.apiKeys.read],
    reason: 'Developer API requires API key read permission',
  },
  '/control-center/commissions': {
    path: '/control-center/commissions',
    requiredAnyPermission: [Permission.settings.commissions.create, Permission.settings.commissions.update],
    reason: 'Commission settings require commission permissions',
  },
  '/control-center/workflows': {
    path: '/control-center/workflows',
    requiredAnyPermission: [Permission.settings.update],
    reason: 'Workflow settings require settings update permission',
  },
  '/control-center/integrations': {
    path: '/control-center/integrations',
    requiredAnyPermission: [Permission.integrations.read],
    reason: 'Integrations require integration read permission',
  },
  '/control-center/audit': {
    path: '/control-center/audit',
    requiredAnyPermission: [Permission.audit.read],
    reason: 'Audit logs require audit read permission',
  },
  // Issue #431 - Gap Analysis: Missing settings routes
  '/control-center/branches': {
    path: '/control-center/branches',
    requiredAnyPermission: [Permission.settings.read],
    reason: 'Branch settings require settings read permission',
  },
  '/control-center/compliance': {
    path: '/control-center/compliance',
    requiredAnyPermission: [Permission.settings.read],
    reason: 'Compliance settings require settings read permission',
  },
  '/control-center/onboarding-wizard': {
    path: '/control-center/onboarding-wizard',
    requiredAnyPermission: [Permission.settings.read],
    reason: 'Onboarding settings require settings read permission',
  },
  '/control-center/entities': {
    path: '/control-center/entities',
    requiredAnyPermission: [Permission.settings.read],
    reason: 'Borrowing entity settings require settings read permission',
  },
  '/control-center/pricing': {
    path: '/control-center/pricing',
    requiredAnyPermission: [Permission.settings.read, Permission.billing.read],
    reason: 'Pricing settings require settings or billing read permission',
  },
  '/control-center/pipeline': {
    path: '/control-center/pipeline',
    requiredAnyPermission: [
      Permission.settings.pipelines.create,
      Permission.settings.pipelines.update,
      Permission.settings.read,
    ],
    reason: 'Pipeline settings require pipeline or settings permission',
  },
  '/control-center/templates': {
    path: '/control-center/templates',
    requiredAnyPermission: [Permission.templates.read],
    reason: 'Template settings require template read permission',
  },
  // Issue #432: Intake flow template management
  '/control-center/intake-templates': {
    path: '/control-center/intake-templates',
    requiredAnyPermission: [Permission.templates.read],
    reason: 'Intake template settings require template read permission',
  },
  '/control-center/enrichment': {
    path: '/control-center/enrichment',
    requiredAnyPermission: [Permission.integrations.read],
    reason: 'Enrichment settings require integration read permission',
  },
  '/control-center/astor/autonomy': {
    path: '/control-center/astor/autonomy',
    requiredAnyPermission: [Permission.agents.read, Permission.agents.configure],
    reason: 'AI autonomy settings require agent read or configure permission',
  },
  '/control-center/business-profile': {
    path: '/control-center/business-profile',
    // Business profile is accessible to all authenticated users within the business
    reason: 'Business profile is accessible to all business members',
  },
  '/control-center/devices': {
    path: '/control-center/devices',
    // Device settings are personal - user can always manage their own devices
    reason: 'Device settings are accessible to all authenticated users',
  },
  '/control-center': {
    path: '/control-center',
    // Control center hub is accessible to all authenticated business users
    reason: 'Control center hub is accessible to all business members',
  },
  '/control-center/preferences': {
    path: '/control-center/preferences',
    // Preferences are personal settings - user can always manage their own
    reason: 'Preferences are accessible to all authenticated users',
  },
  '/control-center/astor/analytics': {
    path: '/control-center/astor/analytics',
    requiredAnyPermission: [Permission.agents.read],
    reason: 'AI analytics require agent read permission',
  },
  '/control-center/business/onboarding': {
    path: '/control-center/business/onboarding',
    requiredAnyPermission: [Permission.settings.read],
    reason: 'Business onboarding requires settings read permission',
  },
  '/control-center/business/profile': {
    path: '/control-center/business/profile',
    // Business profile is accessible to all authenticated users within the business
    reason: 'Business profile is accessible to all business members',
  },
  '/control-center/case-management': {
    path: '/control-center/case-management',
    requiredAnyPermission: [Permission.settings.read],
    reason: 'Loan settings require settings read permission',
  },
  '/control-center/document-checklists': {
    path: '/control-center/document-checklists',
    requiredAnyPermission: [Permission.documents.write],
    reason: 'Document checklist settings require document write permission',
  },
  '/control-center/document-templates': {
    path: '/control-center/document-templates',
    requiredAnyPermission: [Permission.templates.read],
    reason: 'Document template settings require template read permission',
  },
  '/control-center/email-connections': {
    path: '/control-center/email-connections',
    requiredAnyPermission: [Permission.integrations.read],
    reason: 'Email connection settings require integration read permission',
  },
  '/control-center/role-access': {
    path: '/control-center/role-access',
    requiredAnyPermission: [Permission.users.read],
    reason: 'Role access settings require user read permission',
  },
  '/control-center/sizer-policy': {
    path: '/control-center/sizer-policy',
    requiredAnyPermission: [Permission.loanSizer.manageRules],
    reason: 'Sizer policy management requires loan sizer manage_rules permission',
  },
  '/control-center/team': {
    path: '/control-center/team',
    requiredAnyPermission: [Permission.users.read, Permission.teams.manageMembers],
    reason: 'Team settings require user or team read permission',
  },

  // ============================================================================
  // Team Section
  // ============================================================================
  '/team/members': {
    path: '/team/members',
    requiredAnyPermission: [Permission.users.read],
    reason: 'Team member list requires user read permission',
  },
  '/team/teams': {
    path: '/team/teams',
    requiredAnyPermission: [Permission.users.read, Permission.teams.manageMembers],
    reason: 'Teams list requires user or team read permission',
  },
  '/team/org-chart': {
    path: '/team/org-chart',
    requiredAnyPermission: [Permission.users.read],
    reason: 'Org chart requires user read permission',
  },
  '/team/analytics': {
    path: '/team/analytics',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Team analytics require analytics read permission',
  },
  // Issue #1389 - KPI Scorecard
  '/reports/scorecard': {
    path: '/reports/scorecard',
    requiredAnyPermission: [Permission.scorecard.read, Permission.partners.read],
    reason: 'Scorecard requires scorecard or partners read permission',
  },

  // ============================================================================
  // Documents Section (legacy paths — kept so the redirect pages still gate
  // on the same permission as before; users without `documents.read` see the
  // standard Access Denied UX instead of being silently redirected to a
  // surface they can't view either.)
  // ============================================================================
  '/documents': {
    path: '/documents',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'Documents list requires document read permission',
  },
  '/documents/signing': {
    path: '/documents/signing',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'E-sign tracking requires document read permission',
  },
  '/documents/retained-copies': {
    path: '/documents/retained-copies',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'Retained copies require document read permission',
  },

  // ============================================================================
  // Reports Section — historical/audit surfaces moved from legacy /documents.
  // (Active e-signature chasing lives under /inbox/e-signatures next to Tasks.)
  // ============================================================================
  '/reports/e-signatures': {
    path: '/reports/e-signatures',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'E-signature history requires document read permission',
  },
  '/reports/retained-copies': {
    path: '/reports/retained-copies',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'Retained copies require document read permission',
  },
  '/reports/document-approvals': {
    path: '/reports/document-approvals',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'Document approval queue requires document read permission',
  },

  // ============================================================================
  // Inbox Section — active e-sign queue (chase / resend / void).
  // ============================================================================
  '/inbox/e-signatures': {
    path: '/inbox/e-signatures',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'Active e-signature queue requires document read permission',
  },

  // ============================================================================
  // Loans Section - Loan Operations
  // ============================================================================
  '/loans/applications': {
    path: '/loans/applications',
    requiredAnyPermission: [Permission.consumer.piiAccess, Permission.loan.originate],
    reason: 'Applications require PII access or loan origination permission',
  },
  '/loans/borrowers': {
    path: '/loans/borrowers',
    requiredAnyPermission: [Permission.consumer.piiAccess],
    reason: 'Borrower data requires PII access permission',
  },
  '/loans': {
    path: '/loans',
    requiredAnyPermission: [Permission.loan.originate, Permission.loan.process],
    reason: 'Case management requires loan origination or processing permission',
  },
  '/loans/new': {
    path: '/loans/new',
    requiredAnyPermission: [Permission.loan.originate],
    reason: 'Creating cases requires loan origination permission',
  },
  '/loans/create': {
    path: '/loans/create',
    requiredAnyPermission: [Permission.loan.originate],
    reason: 'Creating cases requires loan origination permission',
  },
  // Issue #431 - Gap Analysis: Missing term-sheets route
  '/loans/term-sheets': {
    path: '/loans/term-sheets',
    requiredAnyPermission: [Permission.loan.originate, Permission.loan.process],
    reason: 'Term sheets require loan origination or processing permission',
  },
  '/loans/guarantors': {
    path: '/loans/guarantors',
    requiredAnyPermission: [Permission.consumer.piiAccess],
    reason: 'Guarantor data requires PII access permission',
  },

  // ============================================================================
  // Partners Section (Issue #431 - Gap Analysis)
  // ============================================================================
  '/partners': {
    path: '/partners',
    requiredAnyPermission: [Permission.users.read, Permission.integrations.read],
    reason: 'Partners list requires user or integration read permission',
  },

  // ============================================================================
  // Partnerships Section (Issue #431 - Gap Analysis)
  // Partnerships are capability-gated in nav, but route-level protection added for defense in depth
  // ============================================================================
  '/partnerships': {
    path: '/partnerships',
    requiredAnyPermission: [Permission.users.read],
    reason: 'Partnerships require user read permission',
  },
  '/partnerships/referrals': {
    path: '/partnerships/referrals',
    requiredAnyPermission: [Permission.users.read],
    reason: 'Referrals require user read permission',
  },
  '/partnerships/referral-tracking': {
    path: '/partnerships/referral-tracking',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Referral tracking requires analytics read permission',
  },
  '/partnerships/agreements': {
    path: '/partnerships/agreements',
    requiredAnyPermission: [Permission.documents.read],
    reason: 'Agreements require document read permission',
  },
  '/partnerships/commissions': {
    path: '/partnerships/commissions',
    requiredAnyPermission: [Permission.billing.read],
    reason: 'Commissions require billing read permission',
  },

  // ============================================================================
  // Prospecting Section - Lead Generation & Campaigns
  // ============================================================================
  '/prospecting/discovery': {
    path: '/prospecting/discovery',
    requiredAnyPermission: [Permission.consumer.piiAccess],
    reason: 'Prospect discovery requires PII access permission',
  },
  '/prospecting/leads': {
    path: '/prospecting/leads',
    requiredAnyPermission: [Permission.consumer.piiAccess],
    reason: 'Lead management requires PII access permission',
  },
  '/prospecting/sequences': {
    path: '/prospecting/sequences',
    requiredAnyPermission: [Permission.templates.sequences.read],
    reason: 'Sequences require sequence read permission',
  },
  // Issue #475 - Intake submissions from consumer portal and partner referrals
  '/prospecting/intake-submissions': {
    path: '/prospecting/intake-submissions',
    requiredAnyPermission: [Permission.consumer.piiAccess, Permission.loan.originate],
    reason: 'Intake submissions require PII access or loan origination permission',
  },
  '/prospecting/email': {
    path: '/prospecting/email',
    requiredAnyPermission: [Permission.templates.campaigns.read],
    reason: 'Email campaigns require campaign read permission',
  },
  '/prospecting/email/create': {
    path: '/prospecting/email/create',
    requiredAnyPermission: [Permission.templates.campaigns.create],
    reason: 'Creating campaigns requires campaign create permission',
  },
  '/prospecting/voice': {
    path: '/prospecting/voice',
    requiredAnyPermission: [Permission.templates.campaigns.read],
    reason: 'Voice campaigns require campaign read permission',
  },
  '/prospecting/voice/create': {
    path: '/prospecting/voice/create',
    requiredAnyPermission: [Permission.templates.campaigns.create],
    reason: 'Creating campaigns requires campaign create permission',
  },

  // ============================================================================
  // Notifications Section
  // ============================================================================
  '/notifications/templates': {
    path: '/notifications/templates',
    requiredAnyPermission: [Permission.notifications.templates.read],
    reason: 'Notification templates require template read permission',
  },
  '/notifications/triggers': {
    path: '/notifications/triggers',
    requiredAnyPermission: [Permission.notifications.rules.read],
    reason: 'Notification triggers require rules read permission',
  },
  '/notifications/analytics': {
    path: '/notifications/analytics',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Notification analytics require analytics read permission',
  },

  // ============================================================================
  // Contacts Section (Issue #598 — Unified CRM View)
  // ============================================================================
  '/contacts': {
    path: '/contacts',
    requiredAnyPermission: [Permission.consumer.piiAccess],
    reason: 'Contacts page shows borrower PII data',
  },

  // ============================================================================
  // Payments Section (Issue #596 — Baseline Parity)
  // ============================================================================
  '/payments': {
    path: '/payments',
    requiredAnyPermission: [Permission.billing.read],
    reason: 'Payments require billing read permission',
  },

  // ============================================================================
  // Reports Section (Issue #596 — Baseline Parity)
  // ============================================================================
  '/reports': {
    path: '/reports',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Reports require analytics read permission',
  },
  '/reports/loan-portfolio': {
    path: '/reports/loan-portfolio',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Loan portfolio report requires analytics read permission',
  },
  '/reports/financial': {
    path: '/reports/financial',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Financial report requires analytics read permission',
  },
  '/reports/custom': {
    path: '/reports/custom',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Custom reports require analytics read permission',
  },

  // ============================================================================
  // Funds Section (Issue #596 — Baseline Parity)
  // ============================================================================
  '/funds': {
    path: '/funds',
    requiredAnyPermission: [Permission.billing.read],
    reason: 'Funds require billing read permission',
  },
  '/funds/payouts': {
    path: '/funds/payouts',
    requiredAnyPermission: [Permission.billing.read],
    reason: 'Payouts require billing read permission',
  },

  // ============================================================================
  // Analytics & Billing
  // ============================================================================
  '/analytics': {
    path: '/analytics',
    requiredAnyPermission: [Permission.analytics.read],
    reason: 'Analytics require analytics read permission',
  },
  '/billing': {
    path: '/billing',
    requiredAnyPermission: [Permission.billing.read],
    reason: 'Billing requires billing read permission',
  },
};

// ============================================================================
// Admin Portal Route Permissions
// ============================================================================

/**
 * Route permission mappings for Admin Portal.
 *
 * Admin portal routes typically require platform_admin or super_admin roles.
 * Most admin routes are implicitly protected by the portal's auth context.
 */
export const ADMIN_PORTAL_ROUTE_PERMISSIONS: Record<string, RoutePermissionConfig> = {
  '/security': {
    path: '/security',
    requiredRoles: [UserRole.SUPER_ADMIN],
    reason: 'Security settings require super admin access',
  },
  '/compliance': {
    path: '/compliance',
    requiredRoles: [UserRole.SUPER_ADMIN],
    reason: 'Compliance settings require super admin access',
  },
  '/sandboxes': {
    path: '/sandboxes',
    requiredRoles: [UserRole.SUPER_ADMIN],
    reason: 'Sandbox management requires super admin access',
  },
  '/platform/personas': {
    path: '/platform/personas',
    requiredRoles: [UserRole.SUPER_ADMIN],
    reason: 'Platform persona management requires super admin access',
  },
  '/platform/architecture': {
    path: '/platform/architecture',
    requiredRoles: [UserRole.SUPER_ADMIN],
    reason: 'Platform architecture view requires super admin access',
  },
  '/users': {
    path: '/users',
    requiredRoles: [UserRole.PLATFORM_ADMIN, UserRole.SUPER_ADMIN],
    reason: 'User management requires platform admin or above',
  },
  '/businesses': {
    path: '/businesses',
    requiredRoles: [UserRole.PLATFORM_ADMIN, UserRole.SUPER_ADMIN],
    reason: 'Business management requires platform admin or above',
  },
  '/tenants': {
    path: '/tenants',
    requiredRoles: [UserRole.PLATFORM_ADMIN, UserRole.SUPER_ADMIN],
    reason: 'Tenant management requires platform admin or above',
  },
  '/realms': {
    path: '/realms',
    requiredRoles: [UserRole.PLATFORM_ADMIN, UserRole.SUPER_ADMIN],
    reason: 'Realm management requires platform admin or above',
  },
  '/audit': {
    path: '/audit',
    requiredRoles: [UserRole.PLATFORM_ADMIN, UserRole.SUPER_ADMIN],
    reason: 'Audit log access requires platform admin or above',
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get route permission config for a given path.
 * Supports exact matches and wildcard patterns.
 *
 * @param path - The route path to check
 * @param permissions - The permission map to search
 * @returns The permission config if found, undefined otherwise
 */
export function getRoutePermissionConfig(
  path: string,
  permissions: Record<string, RoutePermissionConfig>,
): RoutePermissionConfig | undefined {
  // Try exact match first
  if (permissions[path]) {
    return permissions[path];
  }

  // Try pattern matching (for dynamic routes like /cases/:id)
  for (const [pattern, config] of Object.entries(permissions)) {
    // Convert route pattern to regex
    // /cases/:id -> /cases/[^/]+
    // /settings/* -> /settings/.*
    const regexPattern = pattern
      .replace(/:[^/]+/g, '[^/]+') // Replace :param with any non-slash chars
      .replace(/\*/g, '.*'); // Replace * with any chars

    const regex = new RegExp(`^${regexPattern}$`);
    if (regex.test(path)) {
      return config;
    }
  }

  return undefined;
}

/**
 * Check if a user has access to a route based on their permissions and roles.
 *
 * @param routeConfig - The route permission configuration
 * @param userPermissions - Set of user's permission strings
 * @param userRole - User's current role
 * @returns true if user has access, false otherwise
 */
export function checkRouteAccess(
  routeConfig: RoutePermissionConfig | undefined,
  userPermissions: Set<string>,
  userRole: string,
): boolean {
  // No config means route is accessible to all authenticated users
  if (!routeConfig) {
    return true;
  }

  // Admin roles bypass all permission checks
  const adminRoles = new Set(['super_admin', 'platform_admin']);
  if (adminRoles.has(userRole)) {
    return true;
  }

  // Check wildcard permission
  if (userPermissions.has('*')) {
    return true;
  }

  // Check role requirements
  if (routeConfig.requiredRoles && routeConfig.requiredRoles.length > 0) {
    const hasRole = routeConfig.requireAllRoles
      ? routeConfig.requiredRoles.every((role) => role === userRole)
      : routeConfig.requiredRoles.some((role) => role === userRole);

    if (!hasRole) {
      return false;
    }
  }

  // Check single permission
  if (routeConfig.requiredPermission) {
    if (!userPermissions.has(routeConfig.requiredPermission)) {
      return false;
    }
  }

  // Check any permission (OR logic)
  if (routeConfig.requiredAnyPermission && routeConfig.requiredAnyPermission.length > 0) {
    const hasAny = routeConfig.requiredAnyPermission.some((perm) => userPermissions.has(perm));
    if (!hasAny) {
      return false;
    }
  }

  // Check all permissions (AND logic)
  if (routeConfig.requiredAllPermissions && routeConfig.requiredAllPermissions.length > 0) {
    const hasAll = routeConfig.requiredAllPermissions.every((perm) => userPermissions.has(perm));
    if (!hasAll) {
      return false;
    }
  }

  return true;
}

// ============================================================================
// Route Permission Registry Validator (Issue #431)
// ============================================================================

/**
 * Validation result for route permission registry
 */
export interface RouteValidationResult {
  /** Whether validation passed */
  valid: boolean;
  /** Routes in nav config missing from route permissions */
  missingRoutePermissions: string[];
  /** Routes in route permissions not found in nav config (orphaned) */
  orphanedRoutePermissions: string[];
  /** Routes with mismatched visibility rules */
  visibilityMismatches: Array<{
    path: string;
    hasNavVisibility: boolean;
    hasRoutePermission: boolean;
  }>;
}

/**
 * Extract all paths from a navigation config
 *
 * @param navItems - Array of navigation items
 * @param settingsCategories - Optional array of settings categories
 * @returns Set of all paths
 */
export function extractNavPaths(
  navItems: Array<{ path: string; tabs?: Array<{ path: string }> }>,
  settingsCategories?: Array<{ path: string }>,
): Set<string> {
  const paths = new Set<string>();

  for (const item of navItems) {
    paths.add(item.path);
    if (item.tabs) {
      for (const tab of item.tabs) {
        paths.add(tab.path);
      }
    }
  }

  if (settingsCategories) {
    for (const category of settingsCategories) {
      paths.add(category.path);
    }
  }

  return paths;
}

/**
 * Validate that all nav routes have corresponding route permission configs.
 *
 * Use this in CI/tests to catch permission gaps early.
 *
 * @param navPaths - Set of paths from navigation config
 * @param routePermissions - Route permission mapping
 * @param options - Validation options
 * @returns Validation result
 *
 * @example
 * ```ts
 * import { BUSINESS_PORTAL_NAV_CONFIG, BUSINESS_PORTAL_SETTINGS_CONFIG } from './configs/business-portal.config';
 * import { BUSINESS_PORTAL_ROUTE_PERMISSIONS, validateRoutePermissions } from './route-permissions';
 *
 * const navItems = BUSINESS_PORTAL_NAV_CONFIG.sections.flatMap(s => s.items);
 * const settingsCategories = BUSINESS_PORTAL_SETTINGS_CONFIG.sections.flatMap(s => s.categories);
 * const navPaths = extractNavPaths(navItems, settingsCategories);
 *
 * const result = validateRoutePermissions(navPaths, BUSINESS_PORTAL_ROUTE_PERMISSIONS);
 * if (!result.valid) {
 *   console.error('Missing route permissions:', result.missingRoutePermissions);
 * }
 * ```
 */
export function validateRoutePermissions(
  navPaths: Set<string>,
  routePermissions: Record<string, RoutePermissionConfig>,
  options: {
    /** Routes that don't need permission configs (e.g., public routes) */
    allowedWithoutPermission?: string[];
    /** Whether to treat orphaned permissions as errors */
    strictOrphans?: boolean;
  } = {},
): RouteValidationResult {
  const { allowedWithoutPermission = [], strictOrphans = false } = options;
  const allowedSet = new Set(allowedWithoutPermission);

  const routePermissionPaths = new Set(Object.keys(routePermissions));

  // Find nav routes missing from route permissions
  const missingRoutePermissions: string[] = [];
  for (const navPath of navPaths) {
    if (!routePermissionPaths.has(navPath) && !allowedSet.has(navPath)) {
      // Check for wildcard matches
      const hasWildcardMatch = Array.from(routePermissionPaths).some((permPath) => {
        if (permPath.includes('*') || permPath.includes(':')) {
          const regex = new RegExp(`^${permPath.replace(/:[^/]+/g, '[^/]+').replace(/\*/g, '.*')}$`);
          return regex.test(navPath);
        }
        return false;
      });

      if (!hasWildcardMatch) {
        missingRoutePermissions.push(navPath);
      }
    }
  }

  // Find orphaned route permissions (defined but not in nav)
  const orphanedRoutePermissions: string[] = [];
  if (strictOrphans) {
    for (const permPath of routePermissionPaths) {
      if (!navPaths.has(permPath)) {
        // Check if it matches any nav path via wildcard
        const hasNavMatch = Array.from(navPaths).some((navPath) => {
          if (permPath.includes('*') || permPath.includes(':')) {
            const regex = new RegExp(`^${permPath.replace(/:[^/]+/g, '[^/]+').replace(/\*/g, '.*')}$`);
            return regex.test(navPath);
          }
          return navPath === permPath;
        });

        if (!hasNavMatch) {
          orphanedRoutePermissions.push(permPath);
        }
      }
    }
  }

  const valid = missingRoutePermissions.length === 0 && orphanedRoutePermissions.length === 0;

  return {
    valid,
    missingRoutePermissions,
    orphanedRoutePermissions,
    visibilityMismatches: [], // Populated by separate visibility validation
  };
}
